import { createIntlTranslations } from 'Features/Intl/fns';
import { Footer } from 'Features/Shared/Components/Footer';
import { Navbar } from 'Features/Shared/Components/Navbar';
import { useDeps } from 'Shared/Application/DepsProvider';
import classNames from 'classnames';
import { GetServerSidePropsContext } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { createEvaluation } from 'v2/http';
import { Meta } from '../src/components/atoms/meta/Meta';
import { StepperLine } from '../src/components/atoms/stepper-line/StepperLine';
import { ValCharacteristics } from '../src/components/molecules/valutatore-stepper/ValCharacteristics';
import { ValConditions } from '../src/components/molecules/valutatore-stepper/ValConditions';
import { ValContatti } from '../src/components/molecules/valutatore-stepper/ValContatti';
import { ValInfo } from '../src/components/molecules/valutatore-stepper/ValInfo';
import { ValLanding } from '../src/components/molecules/valutatore-stepper/ValLanding';
import { ValSellTime } from '../src/components/molecules/valutatore-stepper/ValSellTime';
import { ValSuccesso } from '../src/components/molecules/valutatore-stepper/ValSuccesso';
import { ValTipologia } from '../src/components/molecules/valutatore-stepper/ValTipologia';
import { ApplicationExtendedException } from '../src/core/error';
import {
  EstimatorContactFormFields,
  EstimatorHubspotFormFields,
  Heat,
  PropertyConditions,
} from '../src/core/estimator/estimator.models';
import { usePixelViewContent } from '../src/core/facebook-pixel';
import { useGTMPageView } from '../src/core/gtm/effects/gtmPageView.effect';
import { useLocale } from '../src/core/locales/useLocale';
import {
  sendPixelEstimatorAddress,
  sendPixelEstimatorComplete,
  sendPixelEstimatorConditions,
} from '../src/utils/facebook-pixel';
import { sendGTMEstimator } from '../src/utils/gtm/events/GTMEstimator';
import { DeviceType, isMobileFromUA } from '../src/utils/isMobile';

enum ValutatoreStep {
  Cerca = -1,
  Tipologia = 0,
  Informazioni = 1,
  Condizioni = 2,
  Caratteristiche = 3,
  Tempi = 4,
  Contatti = 5,
  Successo = 6,
}

interface IProps {
  deviceType: DeviceType;
}

export default function Valutatore({ deviceType }: IProps) {
  const { t } = useTranslation(['valutatore']);
  const { t: tc } = useTranslation();
  const router = useRouter();
  const { hubspot } = useDeps();
  const { locale } = useLocale();
  const [activeStep, setActiveStep] = useState<ValutatoreStep>(router.query.location ? 0 : -1);

  const [loading, setLoading] = useState(false);

  //step -1
  const [place, setPlace] = useState<string>(router.query.location?.toString() || '');

  //step 0
  const [typology, setTypology] = useState<string>('');
  //step 1
  const [totalSurface, setTotalSurface] = useState<string>('');
  const [floor, setFloor] = useState<string>('');
  const [totalFloors, setTotalFloors] = useState<string>('');
  const [rooms, setRooms] = useState<string>('');
  const [bathrooms, setBathrooms] = useState<string>('');
  //step 2
  const [conditions, setConditions] = useState<PropertyConditions>('');
  const [propStatus, setPropStatus] = useState<string>('');
  //step 3
  const [heat, setHeat] = useState<Heat>('');
  const [garden, setGarden] = useState<string>('');
  const [balconySurface, setBalconySurface] = useState<string>('');
  const [garageSurface, setGarageSurface] = useState<string>('');
  const [lift, setLift] = useState<boolean>(false);
  //step 4
  const [sellTime, setSellTime] = useState<string>('');

  useGTMPageView(
    () => ({
      event: 'page_view',
      pageType: 'Contatto',
    }),
    [],
  );
  usePixelViewContent(() => ({
    content_type: 'content',
  }));

  const BackButton = () => (
    <div
      className="valutatore__container__button_back"
      onClick={() => setActiveStep(activeStep - 1)}
    >
      ← {tc('prev')}
    </div>
  );

  const handleDeal = async (f: EstimatorContactFormFields) => {
    await hubspot
      .createDealEstimation({ email: f.email })
      .then(() => 'createDealEstimation: success')
      .catch(() => 'createDealEstimation: error');
  };

  const sendData = async (f: EstimatorContactFormFields) => {
    setLoading(true);
    try {
      const formData: EstimatorHubspotFormFields = {
        firstname: f.givenName,
        lastname: f.familyName,
        email: f.email,
        phone: f.phoneNumber,
        private_indirizzo_immobile: place,
        tipologia_immobile: typology,
        superficie__mq_1: totalSurface,
        piano: floor,
        piani_totali: parseInt(totalFloors),
        numero_locali1: parseInt(rooms),
        numero_bagni1: parseInt(bathrooms),
        stato_attuale: propStatus,
        stato_immobile: conditions,
        riscaldamento: heat,
        giardino: garden,
        ascensore: lift,
        tempistiche_vendita: sellTime,
        spazi_esterni: balconySurface,
        posto_auto_mq: garageSurface,
      };
      await createEvaluation(formData);

      await hubspot.createEstimatorFormRecord({
        ...formData,
        consentToProcess: f.flgGeneralConditionPrivacyPolicy.value,
        consentToMarketing: f.flgMarketing.value,
      });

      await handleDeal(f);
      sendGTMEstimator('Step 6 - Info Utente', '');
      sendPixelEstimatorComplete();
      setActiveStep(activeStep + 1);
    } catch (error) {
      throw new ApplicationExtendedException(`Failure estimator post form `, error);
    }
    setLoading(false);
  };

  const renderStepper = (): React.ReactElement => {
    switch (activeStep) {
      case ValutatoreStep.Cerca:
        return (
          <ValLanding
            onSelected={(val) => {
              sendPixelEstimatorAddress();
              sendGTMEstimator('Indirizzo Immobile', window.location.href);
              setPlace(val);
              setActiveStep(activeStep + 1);
            }}
          />
        );
      case ValutatoreStep.Tipologia:
        return (
          <ValTipologia
            onForward={(typology) => {
              sendGTMEstimator('Step 1 - Tipologia', typology);
              setTypology(typology);
              setActiveStep(activeStep + 1);
            }}
          />
        );
      case ValutatoreStep.Informazioni:
        return (
          <ValInfo
            onForward={({ totalSurface, floor, totalFloors, rooms, bathrooms }) => {
              sendGTMEstimator('Step 2 - Informazioni', '');
              setTotalSurface(totalSurface);
              setFloor(floor);
              setTotalFloors(totalFloors);
              setRooms(rooms);
              setBathrooms(bathrooms);
              setActiveStep(activeStep + 1);
            }}
          />
        );
      case ValutatoreStep.Condizioni:
        return (
          <ValConditions
            onForward={({ conditions, propStatus }) => {
              sendPixelEstimatorConditions();
              sendGTMEstimator('Step 3 - Stato e Condizioni', '');
              setConditions(conditions);
              setPropStatus(propStatus);
              setActiveStep(activeStep + 1);
            }}
          />
        );
      case ValutatoreStep.Caratteristiche:
        return (
          <ValCharacteristics
            onForward={({ heat, garden, balconySurface, garageSurface, lift }) => {
              sendGTMEstimator('Step 4 - Caratteristiche', '');
              setHeat(heat);
              setGarden(garden);
              setBalconySurface(balconySurface);
              setGarageSurface(garageSurface);
              setLift(lift);
              setActiveStep(activeStep + 1);
            }}
          />
        );
      case ValutatoreStep.Tempi:
        return (
          <ValSellTime
            onForward={(sellTime) => {
              sendGTMEstimator('Step 5 - Tempi di Vendita', '');
              setSellTime(sellTime);
              setActiveStep(activeStep + 1);
            }}
          />
        );
      case ValutatoreStep.Contatti:
        return (
          <ValContatti
            loading={loading}
            onForward={(f) => {
              sendData(f);
            }}
          />
        );
      case ValutatoreStep.Successo:
        return <ValSuccesso onForward={() => router.push(`/`)} />;
      default:
        return <div>Error</div>;
    }
  };

  return (
    <>
      <Navbar />
      <div className="page_wrapper">
        <Meta {...t('metadata')} />
        <div>
          <div
            className={classNames({
              valutatore__host:
                deviceType === DeviceType.Desktop ||
                (deviceType === DeviceType.Mobile && activeStep === ValutatoreStep.Cerca) ||
                activeStep === ValutatoreStep.Successo,
            })}
          >
            {activeStep > ValutatoreStep.Cerca &&
              activeStep < ValutatoreStep.Successo &&
              deviceType === DeviceType.Desktop && (
                <div className="valutatore__host__container">
                  <div className="valutatore__host__container__title">{t('title_flow')}</div>
                  <div className="valutatore__host__container__subtitle">
                    {t('subtitle2')}
                    <strong>{place}</strong>
                  </div>
                </div>
              )}
            {activeStep > ValutatoreStep.Cerca &&
              activeStep < ValutatoreStep.Successo &&
              deviceType === DeviceType.Mobile && (
                <div className="valutatore__host__container">
                  <div
                    className="valutatore__host__container__back"
                    onClick={() => setActiveStep(activeStep - 1)}
                  >
                    ‹
                  </div>
                  <div className="valutatore__host__container__subtitle">
                    {t('subtitle2')} <br />
                    <strong>{place}</strong>
                  </div>
                </div>
              )}
            <div
              className={
                activeStep > ValutatoreStep.Cerca && activeStep < ValutatoreStep.Successo
                  ? 'valutatore__container'
                  : 'valutatore__container valutatore__container--noBg'
              }
            >
              {activeStep > ValutatoreStep.Cerca && activeStep < ValutatoreStep.Successo && (
                <StepperLine currentStep={activeStep} totalSteps={6} />
              )}
              {renderStepper()}
              {activeStep > ValutatoreStep.Cerca &&
                activeStep < ValutatoreStep.Successo &&
                deviceType === DeviceType.Desktop && <BackButton />}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
  const namespaces = [
    'common',
    'vendere-una-proprieta',
    'valutatore',
    'feedbacks',
    'navbar',
    'footer',
  ];

  const i18n = await serverSideTranslations(context.locale || 'it', namespaces);

  const UA: string | undefined = context.req.headers['user-agent'];
  const deviceType = isMobileFromUA(UA);

  const locale = context.locale?.startsWith('it') ? 'it' : 'en';
  const intl = await createIntlTranslations(locale);

  return {
    props: {
      deviceType,
      ...i18n,
      ...intl,
    },
  };
};
